/**
 * Canonical labels for proposal catalog fields (`category`, `location`) shown in UI.
 * Aligns raw API strings with {@link PROPOSAL_CATEGORIES} / {@link PROPOSAL_LOCATIONS}.
 */

import type { CategoryOption, LocationOption } from '@/config/proposal-options';
import { PROPOSAL_CATEGORIES, PROPOSAL_LOCATIONS } from '@/config/proposal-options';

function resolveByIdOrName<T extends { id: string; name: string }>(
  raw: string,
  rows: readonly T[],
): T | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const lower = trimmed.toLowerCase();
  return rows.find((row) => row.id === lower || row.name.toLowerCase() === lower) ?? null;
}

/** Unknown slug/text — capitalize first letter only; preserve the rest. */
export function capitalizeLeading(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return raw;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function resolveProposalCategory(raw: string): CategoryOption | null {
  return resolveByIdOrName(raw, PROPOSAL_CATEGORIES);
}

/** Common API aliases → canonical PROPOSAL_LOCATIONS.id */
const LOCATION_ALIAS_TO_ID: Record<string, string> = {
  usa: 'us',
  'u.s.': 'us',
  'u.s.a.': 'us',
  uk: 'gb',
  'u.k.': 'gb',
  uae: 'ae',
};

export function resolveProposalLocation(raw: string): LocationOption | null {
  const direct = resolveByIdOrName(raw, PROPOSAL_LOCATIONS);
  if (direct !== null) return direct;

  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const mappedId = LOCATION_ALIAS_TO_ID[trimmed.toLowerCase()];
  if (mappedId !== undefined) {
    return PROPOSAL_LOCATIONS.find((l) => l.id === mappedId) ?? null;
  }
  return null;
}
